import { useCallback, useMemo, useState } from "react";
import { IDropdownOption } from "@fluentui/react/lib/Dropdown";

import { hasNoElements, removeElement, unique } from "Utils/Array";
import { getTranslatedFieldLabel } from "Utils/Field";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace, isString } from "Utils/String";
import { getItemType } from "Utils/Item";
import { isMultiOptionListField } from "Utils/Metadata";
import { IOption, IOptionList } from "Model/Configuration/IOptions";
import { IDependencyOption } from "Model/Configuration/IDependency";
import { IUseEditOption } from "./IUseEditOption";
import { IEditFieldProps } from "../IEditFieldProps";

export function useEditOption(props: IEditFieldProps): IUseEditOption {
  const {
    fieldName, options, dependency, isRequired, item, onChange, translator
  } = props;

  const [selectedKeys, setSelectedKeys] = useState<string[] | number[]>(() => {
    const initialValue = item?.[fieldName] as unknown[];
    if (hasNoElements(initialValue)) {
      return [];
    }

    if (isString(initialValue[0])) {
      return initialValue as string[];
    }

    return initialValue as number[];
  });

  const [selectedKey, setSelectedKey] = useState<IOption>(() => {
    const initialValue = item?.[fieldName] as IOption;
    if (isString(initialValue) && isNullOrWhiteSpace(initialValue)) {
      return null;
    }

    return initialValue;
  });

  const createOption = useCallback(
    (option: IOption) => ({
      key: option,
      text: getTranslatedFieldLabel(`${fieldName}.option.${option}`, getItemType(item), translator)
    }),
    [fieldName, item, translator]
  );

  const generateDependentOptions = useCallback(
    (list: IOptionList) => {
      const parentValue = item[dependency.parent]?.toString();

      if (isNullOrWhiteSpace(parentValue)) {
        return getOptionList(dependency.null, list);
      }

      return getOptionList(dependency.map?.[parentValue], list);
    },
    [dependency, item]
  );

  const dropdownOptions = useMemo(() => {
    if (hasNoElements(options)) {
      return [];
    }

    const list = isNotNullOrWhiteSpace(dependency?.parent) ? generateDependentOptions(options) : options;
    return list.map<IDropdownOption>(createOption);
  }, [generateDependentOptions, createOption, dependency, options]);

  const multiSelect = isMultiOptionListField(props);

  const onChangeValue = useCallback(
    (_, option: IDropdownOption) => {
      const { key, selected } = option;
      if (multiSelect) {
        const keys = selected ? unique([...selectedKeys, key]) : removeElement(selectedKeys, key);
        setSelectedKeys(keys as string[] | number[]);
        onChange(fieldName, keys);
        return;
      }

      setSelectedKey(key as string);
      onChange(fieldName, key);
    },
    [fieldName, multiSelect, onChange, selectedKeys]
  );

  const isValueMissing = multiSelect ? hasNoElements(selectedKeys as unknown[]) : selectedKey == null;
  let errorMessage = null;
  if (isRequired && isValueMissing) {
    errorMessage = translator.get(`generic.missingvalue`, `This value is missing or invalid.`);
  }

  return {
    errorMessage,
    multiSelect,
    onChange: onChangeValue,
    options: dropdownOptions,
    ...(multiSelect ? { selectedKeys } : null),
    ...(!multiSelect ? { selectedKey } : null)
  };
}

function getOptionList(map: IDependencyOption, optionList: IOptionList, nullMap?: IDependencyOption): IOptionList {
  if (hasNoElements(optionList)) {
    return [];
  }

  if (map == null) {
    return nullMap != null ? getOptionList(nullMap, optionList) : [];
  }

  if (isString(map)) {
    return map === `all` ? optionList : [];
  }

  return map;
}
